import { FC, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import FilterGroup, { Filter } from "./FilterGroup"
import { alertFilters, statusFilters } from "./filters"
import { getAlarmFilterKey } from "./getAlarmFilterKey"
import { TableActions } from "./TableActions"
import { wearablesColumns } from "./wearablesColumns"
import { wearablesJmsColumns } from "./wearablesJmsColumns"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { Icon } from "@/components/shared/Icon"
import { Table } from "@/components/shared/Table"
import { useJmsEnabled } from "@/hooks/useJmsEnabled"
import { AppDispatch } from "@/store"
import {
   fetchWearableSensors,
   selectAllWearableSensors,
} from "@/store/wearableSensorsSlice"
import { AlertConfiguration } from "@/types/alert"
import { AlertCategory } from "@/types/alertConfigurations"
import { WearableSensor } from "@/types/wearableSensors"
import "./index.scss"

const FETCH_INTERVAL_MS = 5000

const WearablesOverview: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const wearableSensors = useSelector(selectAllWearableSensors)

   const [alertCheckedById, setAlertCheckedById] = useState<
      Record<string, boolean>
   >(() => Object.fromEntries(alertFilters.map((f) => [f.id, f.checked])))
   const [statusFiltersState, setStatusFiltersState] = useState(statusFilters)
   const jmsEnabled = useJmsEnabled()

   useEffect(() => {
      dispatch(fetchWearableSensors())
      const interval = setInterval(() => {
         dispatch(fetchWearableSensors())
      }, FETCH_INTERVAL_MS)

      return () => clearInterval(interval)
   }, [dispatch])

   const vitalAlertConfigsById = useMemo(() => {
      const configs = new Map<string, AlertConfiguration>()
      wearableSensors.forEach((sensor) => {
         const alertConfiguration = sensor.alarm?.alertConfiguration
         if (alertConfiguration?.category === AlertCategory.VITAL) {
            configs.set(alertConfiguration.id, alertConfiguration)
         }
      })
      return configs
   }, [wearableSensors])

   const dynamicAlertFilters = useMemo<Filter[]>(
      () =>
         Array.from(vitalAlertConfigsById.values()).map((config) => {
            const id = `alert-vital-${config.id}`
            return {
               type: config.id,
               id,
               iconName: null,
               label: config.name,
               checked: alertCheckedById[id] ?? true,
            }
         }),
      [vitalAlertConfigsById, alertCheckedById]
   )

   const alertFiltersState = useMemo(
      () => [
         ...alertFilters.map((f) => ({
            ...f,
            checked: alertCheckedById[f.id] ?? true,
         })),
         ...dynamicAlertFilters,
      ],
      [alertCheckedById, dynamicAlertFilters]
   )

   const selectedAlertTypes = alertFiltersState
      .filter((f) => f.checked)
      .map((f) => f.type)
   const selectedStatusTypes = statusFiltersState
      .filter((f) => f.checked)
      .map((f) => f.type)

   const handleAlertFilterChange = (
      changedFilter: Filter,
      checked: boolean
   ) => {
      setAlertCheckedById((prev) => ({
         ...prev,
         [changedFilter.id]: checked,
      }))
   }

   const handleStatusFilterChange = (
      changedFilter: Filter,
      checked: boolean
   ) => {
      setStatusFiltersState((filters) =>
         filters.map((f) =>
            f.type === changedFilter.type ? { ...f, checked } : f
         )
      )
   }

   const handleClearFilters = () => {
      setAlertCheckedById(
         Object.fromEntries(
            [...alertFilters, ...dynamicAlertFilters].map((f) => [f.id, false])
         )
      )
      setStatusFiltersState(
         statusFilters.map((f) => ({ ...f, checked: false }))
      )
   }

   const filteredData = wearableSensors.filter((item) => {
      const alarmKey = getAlarmFilterKey(item.alarm)
      return (
         (selectedAlertTypes.length === 0 ||
            selectedAlertTypes.includes(alarmKey)) &&
         (selectedStatusTypes.length === 0 ||
            selectedStatusTypes.includes(item.status))
      )
   })

   return (
      <Grid className="wearables-overview">
         <GridItem
            className="wearables-overview__filters"
            size={2}
         >
            <div className="wearables-overview__clear-filters">
               <span
                  className="wearables-overview__clear-filters-title"
                  onClick={handleClearFilters}
               >
                  <Icon
                     size="8"
                     name="broom"
                     className="wearables-overview__clear-filters-icon"
                  />
                  <span>Clear filters</span>
               </span>
            </div>
            <FilterGroup
               groupTitle="Alarms"
               filters={alertFiltersState}
               onChange={handleAlertFilterChange}
            />
            <FilterGroup
               groupTitle="Status"
               filters={statusFiltersState}
               onChange={handleStatusFilterChange}
            />
         </GridItem>
         <GridItem size={10}>
            <Table<WearableSensor>
               className="wearables-overview__table"
               columns={jmsEnabled ? wearablesJmsColumns : wearablesColumns}
               rows={filteredData}
               editable={false}
               extraActions={
                  <TableActions onClick={() => console.log("View clicked")} />
               }
            />
         </GridItem>
      </Grid>
   )
}

export default WearablesOverview
